import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CalendarDays } from "lucide-react";
import { SiteHeader, SiteFooter } from "./SiteLayout";
import { usePageMeta } from "./siteContent";

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

function Block({ b }) {
  if (b.type === "heading") {
    return <h2 className="font-serif-site text-2xl font-bold text-white mt-10">{b.text}</h2>;
  }
  if (b.type === "image") {
    return <img src={b.src} alt="" className="w-full rounded-2xl shadow-lg my-8" />;
  }
  if (b.type === "list") {
    return (
      <ul className="space-y-2.5 my-6">
        {(b.items || []).map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-slate-200">
            <span className="mt-1 inline-flex w-5 h-5 rounded-full bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 items-center justify-center shrink-0">
              <ArrowRight size={11} />
            </span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.type === "quote") {
    return (
      <blockquote className="my-8 border-l-4 border-[#00D2C2] bg-[#00D2C2]/15 rounded-r-xl p-5 text-slate-200 italic">
        {b.text}
      </blockquote>
    );
  }
  return <p className="text-slate-200 leading-relaxed my-5 whitespace-pre-line">{b.text}</p>;
}

export default function SiteBlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  usePageMeta(
    post ? `${post.title} | Wales Event Medical Services` : "News & Updates | Wales Event Medical Services",
    post ? (post.excerpt || "News and updates from Wales Event Medical Services.") : "",
    post ? post.cover_image : "");
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/blog/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("not found"))))
      .then(setPost)
      .catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return undefined;
    const tags = (post.tags || []).filter(Boolean);
    if (tags.length) {
      let el = document.head.querySelector('meta[name="keywords"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "keywords");
        document.head.appendChild(el);
      }
      el.setAttribute("content", tags.join(", "));
    }
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "blog-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: post.title, description: post.excerpt || "",
      image: post.cover_image ? [post.cover_image] : undefined,
      datePublished: post.published_at || undefined,
      keywords: tags.join(", ") || undefined,
      author: { "@type": "Organization", name: "Wales Event Medical Services" },
      publisher: { "@type": "Organization", name: "Wales Event Medical Services" },
    });
    document.head.appendChild(ld);
    return () => document.getElementById("blog-ld")?.remove();
  }, [post]);

  return (
    <div className="site-font bg-[#0B0F14]" data-testid="site-blog-post">
      <SiteHeader />
      {missing ? (
        <section className="py-32 text-center" data-testid="blog-post-missing">
          <h1 className="section-title">Post not found</h1>
          <p className="mt-3 text-slate-300">This post may have been removed or isn't published yet.</p>
          <Link to="/blog" className="btn btn-primary mt-8" data-testid="blog-back">Back to the blog</Link>
        </section>
      ) : !post ? (
        <p className="py-32 text-center text-slate-400 text-sm">Loading…</p>
      ) : (
        <>
          <article className="py-16 md:py-20">
            <div className="container-x max-w-3xl">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#00D2C2] hover:gap-3 transition-all" data-testid="blog-back">
                <ArrowLeft size={16} /> All posts
              </Link>
              <p className="mt-8 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00D2C2] uppercase tracking-widest">
                <CalendarDays size={14} /> {fmtDate(post.published_at)}
              </p>
              <h1 className="font-serif-site mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="blog-post-title">
                {post.title}
              </h1>
              {post.excerpt && <p className="mt-5 text-lg text-slate-300 leading-relaxed">{post.excerpt}</p>}
              {post.cover_image && (
                <img src={post.cover_image} alt={post.title} className="mt-8 w-full max-h-[420px] object-cover rounded-3xl shadow-xl" data-testid="blog-post-cover" />
              )}
              {post.body_html ? (
                <div className="blog-body" data-testid="blog-post-body" dangerouslySetInnerHTML={{ __html: post.body_html }} />
              ) : (
                <div className="mt-10" data-testid="blog-post-body">
                  {(post.blocks || []).map((b, i) => <Block key={i} b={b} />)}
                </div>
              )}
              {(post.tags || []).filter(Boolean).length > 0 && (
                <div className="mt-10 pt-8 border-t border-white/10" data-testid="blog-post-tags">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Topics</p>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.filter(Boolean).map((t, i) => (
                      <span key={i} className="px-3 py-1.5 rounded-full bg-[#00D2C2]/10 border border-[#00D2C2]/25 text-[#00D2C2] text-xs font-semibold" data-testid={`blog-tag-${i}`}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
          <section className="pb-20">
            <div className="container-x max-w-3xl">
              <div className="rounded-3xl bg-gradient-to-br from-[#0F2B32] to-[#0B0F14] border border-[#00D2C2]/25 px-8 py-10 text-center relative overflow-hidden shadow-2xl">
                <h2 className="font-serif-site text-white text-2xl md:text-3xl font-extrabold tracking-tight">Planning an event? Let us keep it safe.</h2>
                <Link to="/intake" className="btn bg-[#00D2C2] text-[#0B0F14] font-bold hover:bg-[#14B8A6] shadow-lg mt-6" data-testid="blog-post-quote">
                  Get a Free Quote <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
      <SiteFooter />
    </div>
  );
}
